/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 */

import { useMemo } from 'react';

import {
  Box,
  useDisclosure,
  Modal,
  useToast,
  useColorModeValue,
  HStack,
  IconButton,
  Tooltip,
  Divider,
  ButtonGroup,
  Text,
} from '@chakra-ui/react';

import { HiChip, HiPuzzle } from 'react-icons/hi';
import { IoSparklesSharp } from 'react-icons/io5';
import {
  MdAdd,
  MdApps,
  MdArrowBack,
  MdArrowForward,
  MdFolder,
  MdHome,
  MdMap,
  MdPeople,
  MdRemove,
  MdRemoveRedEye,
  MdScreenShare,
  MdViewList,
} from 'react-icons/md';

import { format as formatDate } from 'date-fns';
import JSZip from 'jszip';

import {
  ContextMenu,
  downloadFile,
  useAssetStore,
  useAppStore,
  useUIStore,
  useBoardStore,
  useRouteNav,
  useRoomStore,
  useConfigStore,
  Clock,
  useThrottleApps,
  useAbility,
  apiUrls,
  useHotkeys,
  Alfred,
  HotkeysEvent,
  useUserSettings,
  useHexColor,
  useUser,
  useThrottleScale,
  MainButton,
  PartyButton,
  useLinkStore,
} from '@sage3/frontend';
import { SAGEColors } from '@sage3/shared';

import {
  BoardContextMenu,
  ClearBoardModal,
  AppToolbar,
  Twilio,
  LassoToolbar,
  PresenceFollow,
  BoardTitle,
  TagsDisplay,
  Interactionbar,
  ScreenshareMenu,
  ToolbarButton,
  ApplicationsMenu,
  NavigationMenu,
  KernelsMenu,
  PluginsMenu,
  UsersMenu,
  AssetsMenu,
  EscapeApp,
  NarrativePathView,
  PresentationController,
} from './components';

type UILayerProps = {
  boardId: string;
  roomId: string;
};

export function UILayer(props: UILayerProps) {
  // Abilities
  const canLasso = useAbility('lasso', 'apps');

  // Settings
  // const { settings, setPrimaryActionMode } = useUserSettings();
  const { settings } = useUserSettings();
  const showUI = settings.showUI;
  // const primaryActionMode = settings.primaryActionMode;

  // Colors
  const tealColorMode = useColorModeValue('teal.500', 'teal.200');
  const teal = useHexColor(tealColorMode);

  // User
  const { user } = useUser();
  // Hardcoded to teal for visual consistency across all toolbar buttons
  const usersColor = 'teal';

  // Scale
  const scale = useThrottleScale(250);

  // UI Store
  const fitApps = useUIStore((state) => state.fitApps);
  const setClearAllMarkers = useUIStore((state) => state.setClearAllMarkers);
  const selectedApp = useUIStore((state) => state.selectedAppId);
  const setSelectedApp = useUIStore((state) => state.setSelectedApp);
  const savedSelectedAppsIds = useUIStore((state) => state.savedSelectedAppsIds);
  const clearSavedSelectedAppsIds = useUIStore((state) => state.clearSavedSelectedAppsIds);
  const setSelectedAppsIds = useUIStore((state) => state.setSelectedAppsIds);
  const resetZoom = useUIStore((state) => state.resetZoom);
  const zoomIn = useUIStore((state) => state.zoomInDelta);
  const zoomOut = useUIStore((state) => state.zoomOutDelta);
  const isContextMenuOpen = useUIStore((state) => state.contextMenuOpen);
  const narrativePathViewVisible = useUIStore((state) => state.narrativePathViewVisible);
  const setNarrativePathViewVisible = useUIStore((state) => state.setNarrativePathViewVisible);

  // Links store for narrative path tracking
  const links = useLinkStore((state) => state.links);

  // Asset store
  const assets = useAssetStore((state) => state.assets);
  // Board store
  const boards = useBoardStore((state) => state.boards);
  const board = boards.find((el) => el._id === props.boardId);
  // Configuration information
  const config = useConfigStore((state) => state.config);
  // Room Store
  const rooms = useRoomStore((state) => state.rooms);
  const room = rooms.find((el) => el._id === props.roomId);
  // Apps
  const apps = useThrottleApps(250);
  const deleteApp = useAppStore((state) => state.delete);

  // Navigation
  const { toHome, back, forward, canGoBack, canGoForward } = useRouteNav();

  // Toast
  const toast = useToast();

  // Clear board modal
  const { isOpen: clearIsOpen, onOpen: clearOnOpen, onClose: clearOnClose } = useDisclosure();

  // Alfred Modal
  const { isOpen: alfredIsOpen, onOpen: alfredOnOpen, onClose: alfredOnClose } = useDisclosure();

  // Connect to Twilio only if there are Screenshares or Webcam apps
  const twilioConnect = apps.filter((el) => el.data.type === 'Screenshare').length > 0;

  // Unhide UI
  const { toggleShowUI } = useUserSettings();

  /**
   * Clear the board confirmed
   */
  const onClearConfirm = () => {
    // delete all apps
    const ids = apps.map((a) => a._id);
    deleteApp(ids);
    setClearAllMarkers(true);
    // close the modal
    clearOnClose();
  };

  // Show all the application
  const showAllApps = () => {
    fitApps(apps);
  };

  // How to save a board opened files into a ZIP archive
  const downloadRoomAssets = async (ids: string[]) => {
    // Create a ZIP object
    const zip = new JSZip();
    // Generate a filename using date and board name
    const boardName = boards.find((b) => b._id === props.boardId)?.data.name || 'session';
    const prettyDate = formatDate(new Date(), 'yyyy-MM-dd-HH-mm-ss');
    const name = `SAGE3-${boardName.replace(' ', '-')}-${prettyDate}.zip`;
    // Create a folder in the archive
    const session = zip.folder(`SAGE3-${boardName}`);
    // Iterate over all the apps
    await assets.reduce(async (promise, asset) => {
      // wait for the last async function to finish
      await promise;
      // Assets from the room and in the list
      if (asset.data.room === props.roomId && ids.includes(asset._id)) {
        // Derive the public URL
        const url = apiUrls.assets.getAssetById(asset.data.file);
        // Get the filename for the asset
        const filename = asset.data.originalfilename;
        // if all set, add the file to the zip
        if (url && filename && session) {
          // Download the file contents
          const buffer = await fetch(url).then((r) => r.arrayBuffer());
          // add to zip
          session.file(filename, buffer);
        }
      }
    }, Promise.resolve());
    // Display a message
    toast({ title: 'Assets Packaged', status: 'info', duration: 4000, isClosable: true });
    // Finish the zip and trigger the download
    zip.generateAsync({ type: 'blob' }).then(function (content) {
      // Create a URL from the blob
      const url = URL.createObjectURL(content);
      // Trigger the download
      downloadFile(url, name);
      toast({ title: 'Download in Progress', status: 'success', duration: 2000, isClosable: true });
    });
  };

  const downloadBoardAssets = async () => {
    // Create a ZIP object
    const zip = new JSZip();
    // Generate a filename using date and board name
    const boardName = boards.find((b) => b._id === props.boardId)?.data.name || 'session';
    const prettyDate = formatDate(new Date(), 'yyyy-MM-dd-HH-mm-ss');
    const name = `SAGE3-${boardName.replace(' ', '-')}-${prettyDate}.zip`;
    // Create a folder in the archive
    const session = zip.folder(`SAGE3-${boardName}`);
    // Iterate over all the apps
    await savedSelectedAppsIds.reduce(async (promise, id) => {
      // wait for the last async function to finish
      await promise;
      const a = apps.find((a) => a._id === id);
      // process the next app with an asset
      if (a && 'assetid' in a.data.state) {
        const assetid = a.data.state.assetid;
        if (assetid) {
          // Get the asset from the store
          const asset = assets.find((a) => a._id === assetid);
          if (asset) {
            // Derive the public URL
            const url = apiUrls.assets.getAssetById(asset.data.file);
            // Get the filename for the asset
            const filename = asset.data.originalfilename;
            // if all set, add the file to the zip
            if (url && filename && session) {
              // Download the file contents
              const buffer = await fetch(url).then((r) => r.arrayBuffer());
              // add to zip
              session.file(filename, buffer);
            }
          }
        }
      } else if (a && a.data.type === 'Stickie') {
        // Stickies are saved as text files
        if ('text' in a.data.state) {
          const filename = `stickie-${a._id}.txt`;
          if (filename && session) {
            // add to zip
            session.file(filename, a.data.state.text || '');
          }
        }
      }
    }, Promise.resolve());
    // Display a message
    toast({ title: 'Assets Packaged', status: 'info', duration: 4000, isClosable: true });
    // Finish the zip and trigger the download
    zip.generateAsync({ type: 'blob' }).then(function (content) {
      // Create a URL from the blob
      const url = URL.createObjectURL(content);
      //Trigger the download
      downloadFile(url, name);
      toast({ title: 'Download in Progress', status: 'success', duration: 2000, isClosable: true });
    });
  };

  // Deselect all apps when the escape key is pressed
  useHotkeys('esc', () => {
    setSelectedApp('');
    clearSavedSelectedAppsIds();
    setSelectedAppsIds([]);
  });

  // Open Alfred
  useHotkeys('cmd+k,ctrl+k', (ke: KeyboardEvent, he: HotkeysEvent): void | boolean => {
    // Open the window
    alfredOnOpen();
    return false;
  });

  // Redirect the user back to the homepage when clicking the arrow button
  function handleHomeClick() {
    if (room) {
      // Back to the homepage with the room id
      toHome(room._id);
    } else {
      toHome();
    }
    localStorage.removeItem('boardId');
  }

  // Redirect to your previous board
  function handleBackClick() {
    back();
  }

  // Redirect to your next board
  function handleForwardClick() {
    forward();
  }

  const handleOpenAlfred = () => {
    alfredOnOpen();
  };

  // Compute narrative path count for badge display
  const narrativeStepCount = useMemo(() => {
    const boardLinks = links.filter((l) => l.data.boardId === props.boardId && l.data.type === 'narrative_order');
    // Count unique apps in narrative links (source + target)
    const appsInPaths = new Set<string>();
    boardLinks.forEach((l) => {
      appsInPaths.add(l.data.sourceAppId);
      appsInPaths.add(l.data.targetAppId);
    });
    return appsInPaths.size;
  }, [links, props.boardId]);

  // Check if Path View has content (narrative steps OR standalone apps)
  const hasPathViewContent = useMemo(() => {
    return narrativeStepCount > 0 || apps.filter(a => a.data.boardId === props.boardId).length > 0;
  }, [narrativeStepCount, apps, props.boardId]);

  // Toggle narrative path view
  const handleTogglePathView = () => {
    // Read current value directly from store to avoid stale closure in hotkey callback
    const currentVisible = useUIStore.getState().narrativePathViewVisible;
    const newVisible = !currentVisible;
    setNarrativePathViewVisible(newVisible);
    toast({
      title: newVisible ? 'Path View Visible' : 'Path View Hidden',
      status: 'info',
      duration: 1500,
      isClosable: true,
      position: 'top',
    });
  };

  // Keyboard shortcut for path view: 'P' key
  useHotkeys('p', (ke: KeyboardEvent) => {
    ke.preventDefault();
    if (hasPathViewContent) {
      handleTogglePathView();
    }
  });

  return (
    <>
      {/* The bottom right corner showing the visibility icon when the user decides to hide the UI */}
      <HStack position="absolute" bottom="2" right="2" opacity={1} userSelect={'none'}>
        {!showUI && (
          <Tooltip label={'Show Interface'} placement="top-start" shouldWrapChildren={true} openDelay={200} hasArrow={true}>
            <IconButton
              borderRadius="md"
              h="auto"
              p={0}
              mx={-2}
              justifyContent="center"
              aria-label={'Presence'}
              icon={<MdRemoveRedEye size="24px" />}
              background={'transparent'}
              colorScheme="gray"
              transition={'all 0.2s'}
              opacity={0.5}
              variant="ghost"
              onClick={toggleShowUI}
              isDisabled={false}
              _hover={{ color: teal, opacity: 1, transform: 'scale(1.15)' }}
            />
          </Tooltip>
        )}
      </HStack>

      {/* Tool bar at the bottom of the page in the middle */}
      <Box
        position="absolute"
        left="50%"
        bottom="2"
        transform="translateX(-50%)"
        zIndex={101}
        display={showUI ? 'flex' : 'none'}
        borderRadius="md"

      >
        <Box display="flex" gap="1">
          <ButtonGroup isAttached size="xs" gap="0" mr="1">
            <Tooltip label={'Back'}>
              <IconButton
                size="sm"
                icon={<MdArrowBack />}
                fontSize="lg"
                aria-label={'input-type'}
                onClick={handleBackClick}
                isDisabled={!canGoBack}
                sx={{
                  _dark: {
                    bg: 'gray.600', // 'inherit' didnt seem to work
                  },
                }}
              ></IconButton>
            </Tooltip>
            <Tooltip label={'Home'}>
              <IconButton
                size="sm"
                icon={<MdHome />}
                fontSize="lg"
                aria-label={'input-type'}
                onClick={handleHomeClick}
                sx={{
                  _dark: {
                    bg: 'gray.600', // 'inherit' didnt seem to work
                  },
                }}
              ></IconButton>
            </Tooltip>
            <Tooltip label={'Forward'}>
              <IconButton
                size="sm"
                icon={<MdArrowForward />}
                fontSize="lg"
                aria-label={'input-type'}
                onClick={handleForwardClick}
                isDisabled={!canGoForward}
                sx={{
                  _dark: {
                    bg: 'gray.600', // 'inherit' didnt seem to work
                  },
                }}
              ></IconButton>
            </Tooltip>
          </ButtonGroup>
          <Divider orientation="vertical" mx="1" />
          <MainButton
            buttonStyle="solid"
            backToRoom={handleHomeClick}
            boardInfo={{
              boardId: props.boardId,
              roomId: props.roomId,
              boardName: board ? board?.data.name : '',
              roomName: room ? room?.data.name : '',
            }}
            config={config}
          />
          <Divider orientation="vertical" mx="1" />
          <Interactionbar isContextMenuOpen={isContextMenuOpen} />
          <EscapeApp />
          <Divider orientation="vertical" mx="1" />
          <ToolbarButton bgColor={usersColor as SAGEColors} icon={<MdPeople />} tooltip={'Users'} title={'Users'}>
            <UsersMenu boardId={props.boardId} />
          </ToolbarButton>
          <ToolbarButton bgColor={usersColor as SAGEColors} icon={<MdScreenShare />} tooltip={'Screenshares'} title={'Screenshares'}>
            <ScreenshareMenu boardId={props.boardId} roomId={props.roomId} />
          </ToolbarButton>
          <ToolbarButton bgColor={usersColor as SAGEColors} icon={<MdApps />} tooltip={'Applications'} title={'Applications'}>
            {room && board && <ApplicationsMenu roomId={room?._id} boardId={board?._id} />}
          </ToolbarButton>
          <ToolbarButton bgColor={usersColor as SAGEColors} icon={<HiPuzzle />} tooltip={'Plugins'} title={'Plugins'}>
            {room && board && <PluginsMenu roomId={room?._id} boardId={board?._id} />}
          </ToolbarButton>
          <ToolbarButton bgColor={usersColor as SAGEColors} icon={<MdFolder />} tooltip={'Assets'} title={'Assets'} offset={[8, 8]}>
            {room && board && <AssetsMenu roomId={room?._id} boardId={board?._id} downloadRoomAssets={downloadRoomAssets} />}
          </ToolbarButton>
          <ToolbarButton bgColor={usersColor as SAGEColors} icon={<HiChip />} tooltip={'Kernels'} title={'Kernels'}>
            {room && board && <KernelsMenu roomId={room?._id} boardId={board?._id} />}
          </ToolbarButton>
        </Box>
        <Divider orientation="vertical" mx="2" />
        <ButtonGroup isAttached size="xs" gap="0" mr="1">
          <Tooltip label={'Zoom Out'}>
            <IconButton
              size="sm"
              icon={<MdRemove />}
              fontSize="lg"
              aria-label={'input-type'}
              onClick={() => zoomOut(10)}
              sx={{
                _dark: {
                  bg: 'gray.600', // 'inherit' didnt seem to work
                },
              }}
            ></IconButton>
          </Tooltip>
          <Tooltip label={'Reset Zoom'}>
            <IconButton
              size="sm"
              aria-label={'input-type'}
              px="1"
              onClick={resetZoom}
              minWidth="50px"
              maxWidth="50px"
              sx={{
                _dark: {
                  bg: 'gray.600', // 'inherit' didnt seem to work
                },
              }}
              icon={<Text>{(scale * 100).toFixed(0)}%</Text>}
            ></IconButton>
          </Tooltip>

          <Tooltip label={'Zoom In'}>
            <IconButton
              size="sm"
              icon={<MdAdd />}
              fontSize="lg"
              aria-label={'input-type'}
              onClick={() => zoomIn(10)}
              sx={{
                _dark: {
                  bg: 'gray.600', // 'inherit' didnt seem to work
                },
              }}
            ></IconButton>
          </Tooltip>
        </ButtonGroup>
        <ToolbarButton bgColor={usersColor as SAGEColors} icon={<MdMap />} tooltip={'Map'} title={'Map'} offset={[-97, 6]} stayActive>
          <NavigationMenu />
        </ToolbarButton>
        {hasPathViewContent && (
          <Tooltip
            label={narrativeStepCount > 0 ? `Narrative Path (${narrativeStepCount} steps) — [P]` : `Path View (apps on board) — [P]`}
            placement={'top'}
            hasArrow={true}
            openDelay={400}
            shouldWrapChildren={true}
          >
            <IconButton
              colorScheme={narrativePathViewVisible ? 'teal' : 'gray'}
              size="sm"
              icon={<MdViewList />}
              fontSize="lg"
              aria-label={`Toggle Narrative Path View`}
              onClick={handleTogglePathView}
            />
          </Tooltip>
        )}
        <Divider orientation="vertical" mx="2" />
        <Tooltip label={'SAGE Intelligence'} placement={'top'} hasArrow={true} openDelay={400} shouldWrapChildren={true}>
          <IconButton
            colorScheme={'purple'}
            size="sm"
            icon={<IoSparklesSharp />}
            fontSize="lg"
            aria-label={`Open Alfred Menu`}
            onClick={alfredOnOpen}
          />
        </Tooltip>
      </Box>

      {/* Party Button */}
      <Box position="absolute" right="2" bottom="2" zIndex="1000" display={showUI ? 'initial' : 'none'}>
        <PartyButton iconSize="sm" />
      </Box>

      {/* Hub-Room-Board Name Top Left */}
      <Box position="absolute" left="1" top="1" display={showUI ? 'initial' : 'none'} backdropFilter="blur(5px)">
        <BoardTitle room={room} board={board} config={config} />
      </Box>

      {/* The clock Top Right */}
      <Box position="absolute" right="1" top="1" backdropFilter="blur(5px)">
        <Clock isBoard={true} />
      </Box>

      {/* App Toolbar to show when the user selects an app */}
      {selectedApp && <AppToolbar boardId={props.boardId} roomId={props.roomId}></AppToolbar>}

      {/* Right click context menu */}
      <ContextMenu divIds={['board', 'lasso', 'whiteboard']}>
        <BoardContextMenu
          boardId={props.boardId}
          roomId={props.roomId}
          clearBoard={clearOnOpen}
          showAllApps={showAllApps}
          downloadRoomAssets={downloadRoomAssets}
          backHomeClick={handleHomeClick}
          openAlfred={handleOpenAlfred}
        />
      </ContextMenu>

      {/* Clear board dialog */}
      <Modal isCentered isOpen={clearIsOpen} onClose={clearOnClose}>
        <ClearBoardModal onClick={onClearConfirm} onClose={clearOnClose} isOpen={clearIsOpen}></ClearBoardModal>
      </Modal>

      {/* Twilio connection component */}
      <Twilio roomName={props.boardId} connect={twilioConnect} />

      {/* Lasso Toolbar that is shown when apps are selected using the lasso tool */}
      {canLasso && <LassoToolbar downloadAssets={downloadBoardAssets} />}

      {/* Alfred modal dialog */}
      <Alfred boardId={props.boardId} roomId={props.roomId} isOpen={alfredIsOpen} onClose={alfredOnClose} />

      {/* Presence Follow Component: Does not render anything */}
      <PresenceFollow />

      {/* Display a list of all tags */}
      <Box position="absolute" bottom="50" left="2">
        <TagsDisplay />
      </Box>

      {/* Display the narrative path view */}
      <NarrativePathView boardId={props.boardId} />

      {/* Presentation controller (Phase 2) */}
      <PresentationController boardId={props.boardId} />
    </>
  );
}
